import { useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { PageHeader, Card, Alert } from '../../components/ui'
import api from '../../lib/axios'
import toast from 'react-hot-toast'

function Section({ title, description, children }) {
  return (
    <Card className="p-6">
      <div className="mb-5">
        <h2 className="font-semibold text-white text-sm">{title}</h2>
        {description && <p className="text-xs text-white/40 mt-0.5">{description}</p>}
      </div>
      {children}
    </Card>
  )
}

export default function Settings() {
  const { user, updateUser } = useAuth()
  const [profile, setProfile]   = useState({ name: user?.name || '', avatar: user?.avatar || '' })
  const [password, setPassword] = useState({ current_password: '', password: '', password_confirmation: '' })
  const [saving, setSaving]     = useState(false)
  const [pwSaving, setPwSaving] = useState(false)
  const [pwError, setPwError]   = useState('')

  const handleProfile = async e => {
    e.preventDefault()
    setSaving(true)
    try {
      const { data } = await api.put('/profile', profile)
      updateUser(data)
      toast.success('Profile updated!')
    } catch { toast.error('Failed to update profile.') }
    finally { setSaving(false) }
  }

  const handlePassword = async e => {
    e.preventDefault()
    setPwError('')
    setPwSaving(true)
    try {
      await api.put('/profile/password', password)
      toast.success('Password updated. Please log in again.')
      setPassword({ current_password: '', password: '', password_confirmation: '' })
    } catch (err) {
      setPwError(err.response?.data?.errors?.current_password?.[0] || err.response?.data?.message || 'Failed to update password.')
    } finally { setPwSaving(false) }
  }

  return (
    <div className="pb-10 animate-fade-in" style={{ background: 'linear-gradient(180deg, #0d0521 0%, #07020f 100%)', minHeight: '100%' }}>
      <PageHeader title="Settings" subtitle="Manage your account and preferences" />

      <div className="px-8 max-w-2xl space-y-5">
        {/* Profile */}
        <Section title="Profile" description="Update your display name and avatar">
          <form onSubmit={handleProfile} className="space-y-4">
            <div>
              <label className="label-specze">Full name</label>
              <input className="input-specze" value={profile.name} onChange={e => setProfile(p => ({ ...p, name: e.target.value }))} />
            </div>
            <div>
              <label className="label-specze">Avatar URL</label>
              <input className="input-specze" type="url" value={profile.avatar}
                onChange={e => setProfile(p => ({ ...p, avatar: e.target.value }))} placeholder="https://…" />
            </div>
            <button type="submit" disabled={saving} className="btn-specze-sm text-white">
              {saving ? 'Saving…' : 'Save Profile'}
            </button>
          </form>
        </Section>

        {/* Account info */}
        <Section title="Account" description="Your plan and connected providers">
          <div className="space-y-3">
            <div className="flex items-center justify-between py-3 border-b border-white/6">
              <span className="text-sm text-white/50">Email</span>
              <span className="text-sm text-white font-mono">{user?.email}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-white/6">
              <span className="text-sm text-white/50">Plan</span>
              <span className="text-sm capitalize font-semibold"
                style={{ color: user?.plan === 'enterprise' ? '#f472b6' : user?.plan === 'pro' ? '#a855f7' : '#60a5fa' }}>
                {user?.plan}
              </span>
            </div>
            <div className="flex items-center justify-between py-3">
              <span className="text-sm text-white/50">GitHub</span>
              <span className={`text-xs font-semibold ${user?.has_github ? 'text-emerald-400' : 'text-white/30'}`}>
                {user?.has_github ? '✓ Connected' : 'Not connected'}
              </span>
            </div>
          </div>
        </Section>

        {/* Change password */}
        <Section title="Password" description="Update your login password">
          {pwError && <div className="mb-4"><Alert type="error">{pwError}</Alert></div>}
          <form onSubmit={handlePassword} className="space-y-4">
            {['current_password', 'password', 'password_confirmation'].map((k, i) => (
              <div key={k}>
                <label className="label-specze">{['Current password', 'New password', 'Confirm new password'][i]}</label>
                <input className="input-specze" type="password" required
                  value={password[k]} onChange={e => setPassword(p => ({ ...p, [k]: e.target.value }))} />
              </div>
            ))}
            <button type="submit" disabled={pwSaving} className="btn-specze-sm text-white">
              {pwSaving ? 'Updating…' : 'Update Password'}
            </button>
          </form>
        </Section>

        {/* Danger zone */}
        <Section title="Danger Zone" description="Irreversible account actions">
          <div className="p-4 rounded-xl border border-red-500/20 bg-red-500/5">
            <p className="text-sm text-white mb-1 font-semibold">Delete Account</p>
            <p className="text-xs text-white/40 mb-4">This will permanently delete your account and all associated data.</p>
            <button className="text-xs font-semibold text-red-400 hover:text-red-300 border border-red-500/30 hover:bg-red-500/10 px-4 py-2 rounded-xl transition-all">
              Delete my account
            </button>
          </div>
        </Section>
      </div>
    </div>
  )
}
